import React, { useState, useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { MODULES } from '../data/modules';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { resolveApiUrl } from '../apiBase';
import useFooterAwareFabBottom from '../hooks/useFooterAwareFabBottom';
import { streamAnalyze } from '../utils/streamAnalyze';
import KanAIFeedbackWidget from './KanAIFeedbackWidget';
import { KanAIDisclaimer, KanAIInfoButton } from './KanAITransparency';
import {
  KanAIStatusLine,
  KanAIStreamingCaret,
  safeStreamingMarkdown,
} from './kanai-status';
import { useAuth } from '../auth/AuthProvider';
import { apiFetch } from '../api';
// LF-5 (anbudsflödet 2026-10-01): spara ett svar som dokument i ett projekt
import SaveAnswerModal from './kanai/SaveAnswerModal';
import { SaveAnswerButton, SavedArtifactRow } from './kanai/SaveAnswerAction';
import { truncateTitle, previewDocForArtifact } from './kanai/saveAnswerHelpers';
import DocumentPreviewModal from './DocumentPreviewModal';
import useFokusfalla from './kanai/hooks/useFokusfalla';
import useWidgetSamtal from './kanai/hooks/useWidgetSamtal';
import { citationPreviewDoc } from './kanai/citationLinks';
import css from './kanai/kanaiWidgetCss';

/* ── Capability categories (user-facing, hardcoded for full control) ── */
const CAPABILITY_CATEGORIES = [
  {
    key: 'projekt',
    title: 'Projekt & uppgifter',
    items: [
      'Hitta projekt, läsa status, kund, projektledare och datum',
      'Lista aktiva eller försenade aktiviteter',
      'Skapa eller uppdatera uppgifter åt dig',
    ],
  },
  {
    key: 'tidplan',
    title: 'Tidplan & dagbok',
    items: [
      'Sammanfatta dagboksanteckningar och vädernoteringar',
      'Visa aktuell tidplan och avvikelser mot ursprunglig tidplan',
      'Skapa nya dagboksposter med rätt projektkoppling',
    ],
  },
  {
    key: 'ekonomi',
    title: 'Ekonomi & budget',
    items: [
      'Läsa Hogia-data: utfall, leverantörsfakturor, kontosaldon',
      'Prognoser, BÅK-justeringar och budgetjämförelser',
      'Granska fakturor och flagga avvikelser mot inköpsorder',
    ],
  },
  {
    key: 'ata',
    title: 'ÄTA & avvikelser',
    items: [
      'Lista pågående ÄTA-arbeten och deras status',
      'Skapa ÄTA-anmälningar med rätt avtalskoppling',
      'Sammanfatta avvikelser och kvalitetsobservationer',
    ],
  },
  {
    key: 'historikarkiv',
    title: 'Historikarkiv',
    badge: 'NYTT',
    items: [
      'Söka i 200+ historiska Byggsamordnaren-projekt',
      'Läsa gamla dagböcker, leverantörsregister och inköp',
      'Jämföra historiska leverantörer och avstämningar',
    ],
  },
  {
    key: 'dokument',
    title: 'Dokument & ritningar',
    items: [
      'Söka i kunskapsbas, policyer, rutiner och standarder',
      'Hitta ritningar, kontrakt och tekniska beskrivningar',
      'Sammanfatta innehållet i ett dokument',
    ],
  },
  {
    key: 'fordon',
    title: 'Fordon, inventarier & UE',
    items: [
      'Visa fordonsstatus, körjournaler och förmånsvärden',
      'Lista inventarier per projekt eller avdelning',
      'Söka i UE-register: leverantörer, avtal, certifikat',
    ],
  },
];

const apiBaseUrl = resolveApiUrl();
const SNOOZE_KEY = 'kanplan-fab-kanai-snoozed';
const SNOOZE_MS = 30 * 60 * 1000; // 30 min


/* ── Markdown renderer (GFM: tables, lists, headings, hr, ...) ── */
const CITATION_RE = /\[(S?\d+)\]/g;

function renderTextWithCitations(text, oppnaKalla) {
  if (typeof text !== 'string' || !text.includes('[')) return text;
  const out = [];
  let lastIndex = 0;
  let match;
  CITATION_RE.lastIndex = 0;
  while ((match = CITATION_RE.exec(text)) !== null) {
    if (match.index > lastIndex) {
      out.push(text.slice(lastIndex, match.index));
    }
    // Markören öppnar källan när den finns, som på KanAI-sidan (stagetest
    // 2026-10-27: markörerna i hörn-KanAI gick inte att klicka).
    const ref = match[1];
    out.push(oppnaKalla && oppnaKalla.kan(ref) ? (
      <button key={`cit-${match.index}`} type="button" className="kanai-citation kanai-citation--knapp"
        onClick={() => oppnaKalla.oppna(ref)} title="Öppna källan" aria-label={`Öppna källa ${ref}`}>[{ref}]</button>
    ) : (
      <span key={`cit-${match.index}`} className="kanai-citation">[{ref}]</span>
    ));
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) out.push(text.slice(lastIndex));
  return out;
}

const markdownComponents = {
  table: ({ node, ...props }) => (
    <div className="kanai-table-wrap"><table {...props} /></div>
  ),
  a: ({ node, children, ...props }) => (
    <a {...props} target="_blank" rel="noopener noreferrer">{children}</a>
  ),
};

/* Läsbart källnamn (stagekontroll 2026-10-16: källistan visade råa
   serversökvägar som /app/backend/docs/knowledge/.../utlagg.md). */
function kallnamn(c) {
  const sokvag = String(c?.relative_path || c?.path || '');
  const fil = sokvag.split(/[\\/]/).pop() || '';
  const dokument = fil.replace(/\.(md|pdf|docx|txt)$/i, '').replace(/_/g, ' ').trim();
  const avsnitt = String(c?.section_title || '').trim();
  const sida = Number(c?.page) > 0 ? `, s. ${c.page}` : '';
  if (dokument && avsnitt) return `${dokument}: ${avsnitt}${sida}`;
  return `${dokument || avsnitt || 'Dokument'}${sida}`;
}

function renderAnswer(text, oppnaKalla) {
  if (!text) return null;
  const walk = (children) => walkCitations(children, oppnaKalla);
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        ...markdownComponents,
        p: ({ node, children, ...props }) => <p {...props}>{walk(children)}</p>,
        li: ({ node, children, ...props }) => <li {...props}>{walk(children)}</li>,
        td: ({ node, children, ...props }) => <td {...props}>{walk(children)}</td>,
        th: ({ node, children, ...props }) => <th {...props}>{walk(children)}</th>,
      }}
    >
      {text}
    </ReactMarkdown>
  );
}

/* Slår upp en markör ([S7] eller [7]) bland svarets källor. */
function hittaKalla(citations, ref) {
  const r = String(ref);
  return (citations || []).find((c) => c && (String(c.id) === r || `S${c.id}` === r || String(c.id) === r.replace(/^S/, '')));
}

function walkCitations(children, oppnaKalla) {
  return React.Children.map(children, (child, idx) => {
    if (typeof child === 'string') {
      const parts = renderTextWithCitations(child, oppnaKalla);
      if (Array.isArray(parts)) {
        return parts.map((p, i) => (
          typeof p === 'string' ? p : React.cloneElement(p, { key: `cit-${idx}-${i}` })
        ));
      }
      return parts;
    }
    return child;
  });
}

/* Var står användaren? Projekt-id ur adressen (/projects/12/...) och sidans
   namn ur modullistan, så att KanAI svarar om rätt projekt och rätt sida
   (fas E 2026-10-15: tidigare skickades ingen kontext alls). */
function kontextFranAdress(pathname) {
  const p = String(pathname || '');
  const m = p.match(/^\/projects\/(\d+)/);
  const projectId = m ? Number(m[1]) : null;
  let modul = null;
  try {
    const traff = (MODULES || [])
      .filter((x) => x && x.to && (p === x.to || p.startsWith(x.to + '/')))
      .sort((a, b) => b.to.length - a.to.length)[0];
    modul = traff ? traff.label : null;
  } catch { modul = null; }
  if (projectId) {
    const delsida = p.replace(/^\/projects\/\d+\/?/, '').split('/')[0].replace(/^#/, '');
    modul = delsida ? `Projekt, ${delsida}` : 'Projekt';
  }
  return { projectId, modul };
}

const KanaiWidget = forwardRef(function KanaiWidget({ visible = true }, ref) {
  const location = useLocation();
  const { projectId: ctxProjectId, modul: ctxModul } = kontextFranAdress(location.pathname);
  const [open, setOpen] = useState(false);
  // Panelen är en dialog med fokusfälla; fokus tillbaka till knappen vid
  // stängning (U32). Esc sköts av den befintliga lyssnaren.
  const panelRef = useRef(null);
  const fabRef = useRef(null);
  const varOppen = useRef(false);
  useFokusfalla(panelRef, open, null);
  useEffect(() => {
    if (varOppen.current && !open) {
      const t = setTimeout(() => fabRef.current?.focus(), 0);
      varOppen.current = open;
      return () => clearTimeout(t);
    }
    varOppen.current = open;
    return undefined;
  }, [open]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [streamText, setStreamText] = useState('');
  const [conversation, setConversation] = useState([]); // [{role, content, citations?}]
  // Samtalet sparas i KanAI-historiken efter varje svar (F21).
  const { samtalId, sparfel, spara, nyttSamtal } = useWidgetSamtal();
  useEffect(() => {
    const sista = conversation[conversation.length - 1];
    if (!loading && sista?.role === 'assistant' && !sista.error) spara(conversation);
    // Sparas när ett svar är klart; spara byts när samtalet fått sitt id.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation, loading]);
  const [rawChat, setRawChat] = useState([]);
  const [saveTarget, setSaveTarget] = useState(null); // LF-5: {key, title, content, question, sources}
  const [savedArtifacts, setSavedArtifacts] = useState({}); // LF-5: key -> artefakt
  const [artifactPreview, setArtifactPreview] = useState(null);
  // Klickbara markörer i svarstexten öppnar samma förhandsvisning som källistan.
  const kallOppnare = (citations) => ({
    kan: (ref) => Boolean(citationPreviewDoc(hittaKalla(citations, ref) || {}, '')),
    oppna: (ref) => {
      const c = hittaKalla(citations, ref);
      const dok = c && citationPreviewDoc(c, kallnamn(c));
      if (dok) setArtifactPreview(dok);
    },
  });
  const bodyRef = useRef(null);
  const inputRef = useRef(null);
  // Stoppknapp under svar, som på KanAI-sidan (U17).
  const abortRef = useRef(null);
  const navigate = useNavigate();
  const fabBottom = useFooterAwareFabBottom(24);
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'systemadmin' || Boolean(user?.is_admin);

  // Capability panel state (empty-state expandable card)
  const [capsOpen, setCapsOpen] = useState(false);
  const [capsExpanded, setCapsExpanded] = useState({}); // { [key]: bool }
  const [adminRawOpen, setAdminRawOpen] = useState(false);
  const [adminRawTools, setAdminRawTools] = useState(null);
  const [adminRawLoading, setAdminRawLoading] = useState(false);
  const [adminRawError, setAdminRawError] = useState(null);

  const toggleCapExpanded = useCallback((key) => {
    setCapsExpanded(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const loadAdminRaw = useCallback(async () => {
    if (adminRawTools || adminRawLoading) {
      setAdminRawOpen(o => !o);
      return;
    }
    setAdminRawLoading(true);
    setAdminRawError(null);
    setAdminRawOpen(true);
    try {
      const res = await apiFetch(`${apiBaseUrl}/api/kanai/tools/registry`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setAdminRawTools(data?.tools || []);
    } catch (err) {
      setAdminRawError(err?.message || 'Kunde inte ladda tool-registry');
    } finally {
      setAdminRawLoading(false);
    }
  }, [adminRawTools, adminRawLoading]);

  // ── Snooze (right-click to hide for 30 min) ──
  const [snoozed, setSnoozed] = useState(() => {
    const until = localStorage.getItem(SNOOZE_KEY);
    return until && Date.now() < Number(until);
  });

  useEffect(() => {
    if (!snoozed) return;
    const until = Number(localStorage.getItem(SNOOZE_KEY) || 0);
    const remaining = until - Date.now();
    if (remaining <= 0) { setSnoozed(false); return; }
    const timer = setTimeout(() => setSnoozed(false), remaining);
    return () => clearTimeout(timer);
  }, [snoozed]);

  const handleSnooze = useCallback((e) => {
    e.preventDefault();
    localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MS));
    setSnoozed(true);
  }, []);

  const handleWake = useCallback(() => {
    localStorage.removeItem(SNOOZE_KEY);
    setSnoozed(false);
  }, []);

  // Expose .open() to parent via ref
  useImperativeHandle(ref, () => ({
    open: () => setOpen(true),
  }), []);

  // Scroll to bottom when new messages appear
  useEffect(() => {
    if (open && bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [conversation, loading, open]);

  // Focus input when opening
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  // Escape to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') { setOpen(false); e.preventDefault(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const handleSend = useCallback(async () => {
    const q = query.trim();
    if (!q || loading) return;
    setQuery('');

    const userMsg = { role: 'user', content: q };
    setConversation(prev => [...prev, userMsg]);
    setLoading(true);
    setStreamText('');
    let delsvar = '';

    try {
      const body = {
        contextType: ctxProjectId ? 'project' : 'company',
        projectId: ctxProjectId,
        module: ctxModul,
        modeId: 'kanai_project_coach',
        input: { form: {}, freeText: q, documentIds: [] },
        ...(rawChat.length ? { messages: rawChat } : {}),
      };
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      const data = await streamAnalyze(apiBaseUrl + '/api/kanai/analyze', body, {
        onText: (t) => { delsvar = t; setStreamText(t); },
        signal: ctrl.signal,
      });

      // Extract answer text
      const answerLines = data.answerSections?.Svar || [];
      let answerText = answerLines.join('\n') || data.summaryPoints?.map(s => `• ${s}`).join('\n') || 'Inget svar tillgängligt.';
      if (data.stopped === 'max_tokens' || data.stopped === 'max_rounds') {
        answerText += '\n\n*Svaret avbröts innan det var klart. Skriv "fortsätt" för att få resten.*';
      } else if (data.stopped === 'refusal') {
        answerText += '\n\n*KanAI avböjde att besvara den här frågan.*';
      }
      const citations = data.citations || [];
      const aiMsg = { role: 'assistant', content: answerText, citations, auditLogId: data.auditLogId || null };
      setConversation(prev => [...prev, aiMsg]);
      if (data.rawChat) setRawChat(data.rawChat);
    } catch (err) {
      if (err?.name === 'AbortError') {
        setConversation(prev => [...prev, {
          role: 'assistant',
          content: (delsvar ? `${delsvar}\n\n` : '') + '*Du stoppade svaret.*',
        }]);
        return;
      }
      // Nätverksfel (TypeError från fetch) → anslutningstext. Serverns
      // svenska besked (429 kvot, 503 "KanAI startar") visas som de är (U5).
      let text = 'Kunde inte nå KanAI. Kontrollera anslutningen.';
      if (!(err instanceof TypeError) && err?.message) {
        text = err.message.startsWith('HTTP ')
          ? 'KanAI svarade med ett fel. Försök igen om en stund.'
          : err.message;
      }
      setConversation(prev => [...prev, { role: 'assistant', content: text, error: true }]);
    } finally {
      abortRef.current = null;
      setLoading(false);
      setStreamText('');
      // Fokus tillbaka till skrivfältet efter svaret (U17).
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [query, loading, rawChat, ctxProjectId, ctxModul]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleNewChat = () => {
    nyttSamtal();
    setConversation([]);
    setRawChat([]);
    setQuery('');
    inputRef.current?.focus();
  };

  if (!visible) return null;

  return (
    <>
      <style>{css}</style>

      {/* FAB button */}
      {!open && (
        <button
          ref={fabRef}
          type="button"
          className={`kanai-widget-fab ${snoozed ? 'kanai-widget-fab--snoozed' : ''}`}
          style={{ '--kanai-widget-fab-bottom': `${fabBottom}px` }}
          onClick={snoozed ? handleWake : () => setOpen(true)}
          onContextMenu={!snoozed ? handleSnooze : undefined}
          aria-label={snoozed ? 'Visa KanAI igen' : 'Öppna KanAI'}
          title={snoozed ? 'Klicka för att visa igen' : 'KanAI — AI-assistent. Högerklicka för att dölja 30 min'}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M7 2.5l1.3 3.2 3.2 1.3-3.2 1.3L7 11.5 5.7 8.3 2.5 7l3.2-1.3L7 2.5z" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinejoin="round"/>
            <path d="M17 5.5l.9 2.2 2.2.9-2.2.9L17 12.5l-.9-2.2-2.2-.9 2.2-.9.9-2.2z" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinejoin="round"/>
            <path d="M14 15.5l1.1 2.6 2.6 1.1-2.6 1.1L14 23.5l-1.1-2.6-2.6-1.1 2.6-1.1 1.1-2.6z" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinejoin="round"/>
          </svg>
        </button>
      )}

      {/* Backdrop */}
      <div
        className={`kanai-widget-backdrop ${open ? 'open' : ''}`}
        onClick={() => setOpen(false)}
      />

      {/* Panel */}
      <div className="kanai-widget-ram">
      <div
        ref={panelRef}
        className={`kanai-widget-panel ${open ? 'open' : ''}`}
        role="dialog"
        aria-modal={open ? 'true' : undefined}
        aria-label="KanAI"
        aria-hidden={open ? undefined : 'true'}
      >
        {/* Header */}
        <div className="kanai-widget-header">
          <h3>
            <svg className="kanai-widget-ai-ikon" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M7 2.5l1.3 3.2 3.2 1.3-3.2 1.3L7 11.5 5.7 8.3 2.5 7l3.2-1.3L7 2.5z" stroke="currentColor" strokeWidth="2" fill="none" strokeLinejoin="round"/>
              <path d="M17 5.5l.9 2.2 2.2.9-2.2.9L17 12.5l-.9-2.2-2.2-.9 2.2-.9.9-2.2z" stroke="currentColor" strokeWidth="2" fill="none" strokeLinejoin="round"/>
            </svg>
            KanAI
            <span className="ai-badge">AI</span>
            <KanAIInfoButton />
          </h3>
          <div style={{ display: 'flex', gap: 4 }}>
            {conversation.length > 0 && (
              <button type="button" className="kanai-widget-close" onClick={handleNewChat} title="Ny konversation" aria-label="Ny konversation">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M12 5v14M5 12h14" />
                </svg>
              </button>
            )}
            <button type="button" className="kanai-widget-close" onClick={() => setOpen(false)} title="Stäng" aria-label="Stäng KanAI">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="kanai-widget-body" ref={bodyRef}>
          {conversation.length === 0 && !loading && (
            <div className="kanai-widget-empty">
              <svg className="kanai-widget-ai-ikon" width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M7 2.5l1.3 3.2 3.2 1.3-3.2 1.3L7 11.5 5.7 8.3 2.5 7l3.2-1.3L7 2.5z" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinejoin="round"/>
                <path d="M17 5.5l.9 2.2 2.2.9-2.2.9L17 12.5l-.9-2.2-2.2-.9 2.2-.9.9-2.2z" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinejoin="round"/>
                <path d="M14 15.5l1.1 2.6 2.6 1.1-2.6 1.1L14 23.5l-1.1-2.6-2.6-1.1 2.6-1.1 1.1-2.6z" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinejoin="round"/>
              </svg>
              <p>Ställ en fråga till KanAI om byggbranschens regler, AMA-koder eller dina kunskapsdokument.</p>

              {/* Capability panel */}
              <div className={`kanai-caps ${capsOpen ? 'open' : ''}`} style={{ width: '100%', textAlign: 'left' }}>
                <button
                  type="button"
                  className="kanai-caps-header"
                  onClick={() => setCapsOpen(o => !o)}
                  aria-expanded={capsOpen}
                  style={{ border: 'none', width: '100%' }}
                >
                  <span className="kanai-caps-title">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                      <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                    Vad kan jag hjälpa till med?
                  </span>
                  <svg className="kanai-caps-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>

                {capsOpen && (
                  <div className="kanai-caps-list">
                    {CAPABILITY_CATEGORIES.map(cat => {
                      const isExpanded = !!capsExpanded[cat.key];
                      return (
                        <div key={cat.key} className={`kanai-cap-item ${isExpanded ? 'open' : ''}`}>
                          <button
                            type="button"
                            className="kanai-cap-row"
                            onClick={() => toggleCapExpanded(cat.key)}
                            aria-expanded={isExpanded}
                          >
                            <span className="kanai-cap-row-title">
                              {cat.title}
                              {cat.badge && <span className="kanai-cap-row-badge">{cat.badge}</span>}
                            </span>
                            <svg className="kanai-cap-row-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="6 9 12 15 18 9" />
                            </svg>
                          </button>
                          {isExpanded && (
                            <div className="kanai-cap-body">
                              <ul>
                                {cat.items.map((it, idx) => (<li key={idx}>{it}</li>))}
                              </ul>
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {isAdmin && (
                      <div className="kanai-caps-admin">
                        <button
                          type="button"
                          className="kanai-caps-admin-btn"
                          onClick={loadAdminRaw}
                        >
                          {adminRawOpen ? 'Dölj teknisk information' : 'Visa teknisk information'}
                        </button>
                        {adminRawOpen && (
                          <div className="kanai-caps-admin-raw">
                            {adminRawLoading && <div>Laddar verktygslistan …</div>}
                            {adminRawError && <div style={{ color: 'var(--kp-red-ink, #B91C1C)' }}>{adminRawError}</div>}
                            {!adminRawLoading && !adminRawError && Array.isArray(adminRawTools) && adminRawTools.map(t => (
                              <div key={t.name} className="tool">
                                <span className="tool-name">{t.name}</span>
                                <span className="tool-tier">{t.tier || '—'}</span>
                                <div className="tool-desc">{t.description}</div>
                              </div>
                            ))}
                            {!adminRawLoading && !adminRawError && Array.isArray(adminRawTools) && adminRawTools.length === 0 && (
                              <div>Inga verktyg registrerade.</div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {conversation.map((msg, i) => (
            <div key={i}>
              {msg.role === 'user' ? (
                <div className="kanai-widget-question">{msg.content}</div>
              ) : (
                <>
                  <div className={`kanai-widget-answer${msg.error ? ' fel' : ''}`}>
                    {renderAnswer(msg.content, kallOppnare(msg.citations))}
                  </div>
                  {msg.citations?.length > 0 && (
                    <div className="kanai-widget-sources">
                      <strong>Källor</strong>
                      <ul>
                        {msg.citations.slice(0, 6).map((c, ci) => {
                          // Källan öppnas i förhandsvisningen, som på KanAI-sidan (U33).
                          const dok = citationPreviewDoc(c, kallnamn(c));
                          return (
                            <li key={ci}>
                              <span className="kanai-citation">[{c.id || ci + 1}]</span>{' '}
                              {dok ? (
                                <button type="button" className="kanai-widget-kalla" onClick={() => setArtifactPreview(dok)}
                                  title="Öppna källan">
                                  {kallnamn(c)}
                                </button>
                              ) : kallnamn(c)}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                  <KanAIFeedbackWidget auditLogId={msg.auditLogId} />
                  {!msg.error && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
                      <SaveAnswerButton
                        projectId={null}
                        onClick={() => {
                          const fraga = [...conversation.slice(0, i)].reverse().find((m) => m.role === 'user')?.content || '';
                          setSaveTarget({
                            key: i,
                            title: truncateTitle(fraga || 'KanAI-svar'),
                            content: msg.content || '',
                            question: fraga,
                            sources: (msg.citations || []).filter((cit) => cit && cit.document_id).map((cit) => ({
                              document_id: cit.document_id, page: cit.page ?? null,
                              label: cit.section_title || cit.relative_path || cit.path || '',
                            })),
                          });
                        }}
                      />
                      {savedArtifacts[i] && (
                        <SavedArtifactRow artifact={savedArtifacts[i]} onOpen={() => setArtifactPreview(previewDocForArtifact(savedArtifacts[i]))} />
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          ))}

          {loading && !streamText && (
            <div style={{ display: 'flex', padding: '8px 0' }}>
              <KanAIStatusLine text="KanAI tänker" />
            </div>
          )}
          {loading && streamText && (
            <div className="kanai-widget-answer">
              {renderAnswer(safeStreamingMarkdown(streamText))}
              <KanAIStreamingCaret />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="kanai-widget-footer">
          <div style={{ fontSize: 11, lineHeight: 1.4, color: 'var(--kp-text-muted, #64748b)', padding: '0 2px 6px' }}>
            KanAI är en AI-assistent — förslag genereras av AI och bör alltid granskas.
          </div>
          <div className="kanai-widget-input-row">
            <textarea
              ref={inputRef}
              className="kanai-widget-input"
              placeholder="Ställ en fråga..."
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
              disabled={loading}
            />
            {loading ? (
              <button
                type="button"
                className="kanai-widget-send"
                onClick={() => abortRef.current?.abort()}
                title="Stoppa svaret"
                aria-label="Stoppa svaret"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <rect x="6" y="6" width="12" height="12" rx="2" />
                </svg>
              </button>
            ) : (
              <button
                type="button"
                className="kanai-widget-send"
                onClick={handleSend}
                disabled={!query.trim()}
                title="Skicka"
                aria-label="Skicka frågan"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 2L11 13" />
                  <path d="M22 2l-7 20-4-9-9-4 20-7z" />
                </svg>
              </button>
            )}
          </div>
          {sparfel && (
            <div className="kanai-widget-sparfel" role="status">Samtalet kunde inte sparas i historiken.</div>
          )}
          <a className="kanai-widget-footer-link" href="/kanai" onClick={(e) => {
            e.preventDefault();
            setOpen(false);
            navigate(samtalId ? `/kanai?konversation=${encodeURIComponent(samtalId)}` : '/kanai');
          }}>
            {samtalId ? 'Fortsätt samtalet i KanAI' : 'Öppna fullständig KanAI-vy'}
          </a>
        </div>
      </div>
      </div>

      {/* LF-5: spara svaret som dokument i ett projekt som användaren väljer */}
      <SaveAnswerModal
        open={Boolean(saveTarget)}
        onClose={() => setSaveTarget(null)}
        projectId={null}
        defaultTitle={saveTarget?.title}
        contentMarkdown={saveTarget?.content}
        question={saveTarget?.question}
        sources={saveTarget?.sources}
        modeId="kanai_project_coach"
        onSaved={(artifact) => {
          if (saveTarget) setSavedArtifacts((prev) => ({ ...prev, [saveTarget.key]: artifact }));
          setSaveTarget(null);
        }}
      />
      <DocumentPreviewModal open={Boolean(artifactPreview)} onClose={() => setArtifactPreview(null)} document={artifactPreview} />

      {/* EU AI Act art. 50: engångsupplysning per session om att KanAI är AI */}
      {open && <KanAIDisclaimer />}
    </>
  );
});

export default KanaiWidget;
